import * as AlertDialogPrimitive from "@radix-ui/react-alert-dialog";
import * as React from "react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

/**
 * Confirming something that cannot be taken back. Separate from Dialog because the semantics
 * differ: this traps focus on the *cancel* action by default and cannot be dismissed by clicking
 * outside, which is what you want before deleting somebody's account — or before a mis-click
 * rings a real person's phone (`softphone/dial-number.tsx`). `destructive` colours the button
 * red; not every irreversible act is a deletion.
 */
export function ConfirmDialog({
  open, onOpenChange, title, description, confirmLabel = "Confirm", cancelLabel = "Cancel",
  onConfirm, destructive = false, loading = false, confirmDisabled = false, onCloseAutoFocus,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description: React.ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  onConfirm: () => void;
  destructive?: boolean;
  loading?: boolean;
  /**
   * The action cannot be carried out right now — the dialog still opens and `description` says
   * why. Dimming the button beside the reason is the honest pairing: hiding the control instead
   * would leave somebody clicking a dead number and wondering, and refusing to open the dialog
   * at all would leave them with no reason.
   */
  confirmDisabled?: boolean;
  /** Where focus goes once it closes; Radix's own choice unless the handler prevents it. */
  onCloseAutoFocus?: (event: Event) => void;
}) {
  return (
    <AlertDialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <AlertDialogPrimitive.Portal>
        <AlertDialogPrimitive.Overlay
          className={cn(
            "fixed inset-0 z-50 bg-gray-12/25 backdrop-blur-[1px]",
            "data-[state=open]:animate-in data-[state=open]:fade-in-0",
          )}
        />
        <AlertDialogPrimitive.Content
          onCloseAutoFocus={onCloseAutoFocus}
          className={cn(
            "fixed left-1/2 top-1/2 z-50 w-[calc(100vw-2rem)] max-w-md -translate-x-1/2",
            "-translate-y-1/2 rounded-xl border border-border bg-surface p-5 shadow-lg",
            "data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95",
          )}
        >
          <AlertDialogPrimitive.Title className="text-lg font-semibold text-text">
            {title}
          </AlertDialogPrimitive.Title>
          <AlertDialogPrimitive.Description className="mt-1.5 text-sm text-text-muted">
            {description}
          </AlertDialogPrimitive.Description>
          <div className="mt-5 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
            <AlertDialogPrimitive.Cancel asChild>
              <Button variant="outline" disabled={loading}>
                {cancelLabel}
              </Button>
            </AlertDialogPrimitive.Cancel>
            <Button
              variant={destructive ? "danger" : "default"}
              loading={loading}
              disabled={confirmDisabled}
              onClick={(e) => {
                // Not AlertDialog.Action: that closes the dialog on click, which would tear the
                // spinner down mid-request and leave the user unsure whether it worked.
                e.preventDefault();
                onConfirm();
              }}
            >
              {confirmLabel}
            </Button>
          </div>
        </AlertDialogPrimitive.Content>
      </AlertDialogPrimitive.Portal>
    </AlertDialogPrimitive.Root>
  );
}
